import {
  ContentContainer,
  Icon,
  useToasts,
} from "@twinkltech/curriculum-architect-component-library";
import {
  ApiError,
  ApiErrorResponse,
  ApiResponse,
  ConflictingPlanErrorResponse,
  ConflictingPlanErrorResponseSchema,
  UpdateSubjectPlanPayload,
  UpdateSubjectPlanResponse,
  UpdateSubjectStatusPayload,
  type Curriculum,
  type Subject,
} from "@twinkltech/curriculum-architect-domain-hooks";
import { useTwinklAnalytics } from "@twinkltech/web-analytics-ts";
import { Button, IconActionsSettings, Text } from "@twinkltech/web-ui";
import { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useBlocker } from "react-router-dom";

import { ConflictResolutionModal } from "@/components/ConflictResolutionModal/ConflictResolutionModal";
import { Droppable } from "@/components/Droppable/Droppable";
import { GenericError } from "@/components/GenericError/GenericError";
import { PlannerSettingsModalContainer } from "@/components/PlannerSettingsModal/PlannerSettingsModal.container";
import { ResourceFilters } from "@/components/ResourceFilters/ResourceFilters";
import { SubjectPlanHeader } from "@/components/SubjectPlanHeader/SubjectPlanHeader";
import { TableUnitCard } from "@/components/TableUnitCard/TableUnitCard";
import { UnitDrawer } from "@/components/UnitDrawer/UnitDrawer";
import { UnsavedChangesModal } from "@/components/UnsavedChangesModal/UnsavedChangesModal";

import { useCellBoundaries } from "@/hooks/useCellBoundaries/useCellBoundaries";
import { useSkipCells } from "@/hooks/useSkipCells/useSkipCells";

import { FormattedCellKey } from "@/pages/EditSubjectPlan/types/types";

import type {
  BatchUpdateTableStructuresPayload,
  TableData,
  TableResource,
} from "@/states/unitTableReducer/unitTableReducer";

import { mapTableDataToUpdatePlanPayload } from "@/utils/mapTableDataToUpdatePlanPayload/mapTableDataToUpdatePlanPayload";
import { captureAppError } from "@/utils/monitoring/sentry";

import { COMMON_NS } from "@/i18n/common/common.i18n";
import { useFeedback } from "@/providers/Feedback/Feedback.context";

import { Z_INDEX_ORDERS } from "../config/zIndexOrders.const";

import { EDIT_SUBJECT_PLAN_NS } from "./EditSubjectPlan.i18n";

import type { BlockerFunction } from "react-router-dom";


export const EditSubjectPlan = ({
  canRedo,
  canUndo,
  clientVersion,
  curriculum,
  error,
  handleRefreshSubjectPlan,
  isLoading = false,
  isUpdating = false,
  onAddCustomUnit,
  onBatchSettingsUpdate,
  onRedo,
  onRemoveUnit,
  onSaveSubjectPlan,
  onUndo,
  onUpdateCustomUnit,
  onUpdateSubjectStatus,
  onUpsertAdaptationNote,
  resources,
  subject,
  subjectPlanId,
  tableData,
}: {
  canUndo?: boolean;
  canRedo?: boolean;
  clientVersion?: string;
  curriculum?: Curriculum | null;
  error?: ApiErrorResponse | null;
  handleRefreshSubjectPlan?: () => Promise<void>;
  isLoading?: boolean;
  isUpdating?: boolean;
  onAddCustomUnit: (payload: {
    row: string;
    column: string;
    span?: number;
    title: string;
    description?: string;
  }) => void;
  onBatchSettingsUpdate: (payload: BatchUpdateTableStructuresPayload) => void;
  onRedo: () => void;
  onRemoveUnit: (payload: { cellId: string }) => void;
  onSaveSubjectPlan: ({
    data,
  }: {
    data: UpdateSubjectPlanPayload;
  }) => ApiResponse<
    UpdateSubjectPlanResponse | ConflictingPlanErrorResponse | void
  >;
  onUndo: () => void;
  onUpdateCustomUnit: (data: {
    id: string;
    row: string;
    column: string;
    span?: number;
    title: string;
    description?: string;
  }) => void;
  onUpdateSubjectStatus: (params: {
    curriculumId: string;
    subjectId: string;
    payload: UpdateSubjectStatusPayload;
  }) => ApiResponse<Partial<Subject | void>>;
  onUpsertAdaptationNote: (payload: {
    cellId: string;
    adaptationNote: string;
  }) => void;
  resources: TableResource[];
  subject?: Subject;
  subjectPlanId?: string;
  tableData: Pick<TableData, "columns" | "rows" | "cells">;
}) => {
  const { t } = useTranslation(EDIT_SUBJECT_PLAN_NS);
  const { incrementEngagementCount } = useFeedback();
  const { click } = useTwinklAnalytics();

  const [conflictingData, setConflictingData] =
    useState<ConflictingPlanErrorResponse | null>(null);

  const { addErrorToast, addSuccessToast } = useToasts();
  // Define styles for the sticky header and first column
  const theadBorderStyles = [
    "sticky top-0 bg-white",
    Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.STICKY_COLUMN_HEADERS,
  ].join(" ");
  const [isPlannerSettingsModalOpen, setIsPlannerSettingsModalOpen] =
    useState(false);

  const tableRef = useRef<HTMLTableElement>(null);

  // Not the best way to do this, but avoids a lot of refactoring for now.
  // I think it would be better for unsaved changes to be tracked in the reducer state.
  // But for now, this will do.
  const hasUnsavedChanges = Boolean(canUndo || canRedo);

  const shouldBlock: BlockerFunction = ({ currentLocation, nextLocation }) => {
    if (!hasUnsavedChanges) return false;

    // If pathnames are different, we should block
    if (nextLocation.pathname !== currentLocation.pathname) {
      return true;
    }

    // If we get here, it's the same path, so we don't block
    // (allowing search param changes)
    return false;
  };

  const {
    proceed: allowNavigation,
    reset: abandonNavigation,
    state: blockState,
  } = useBlocker(shouldBlock);

  const firstColumnStyles = [
    "sticky left-0 bg-white overflow-hidden truncate w-[170px]",
    Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.STICKY_ROW_HEADERS,
  ].join(" ");

  const orderedColumns = [...tableData.columns].sort(
    (a, b) => a.order - b.order
  );

  const orderedRows = [...tableData.rows].sort((a, b) => a.order - b.order);

  const { cellBoundaries } = useCellBoundaries({
    cells: tableData.cells,
    orderedColumns,
    orderedRows: tableData.rows,
  });

  // Use the skipCells hook to determine which cells to skip rendering
  const { skipSpannedCells } = useSkipCells(tableData.cells, orderedColumns);

  const onPlannerSettingsModalApply = (
    data: BatchUpdateTableStructuresPayload
  ) => {
    // Dispatch the batch update
    onBatchSettingsUpdate(data);
    setIsPlannerSettingsModalOpen(false);
  };

  const handleSave = async (params?: { override?: boolean }) => {
    const { override = false } = params || {};

    click("click_cta", {
      ctaCopy: "Save",
      clickLocation: "Grid View",
    });

    // reset conflicting data on each save attempt
    setConflictingData(null);

    if (
      !clientVersion ||
      !subject ||
      !subjectPlanId ||
      !tableData.rows.length ||
      !tableData.columns.length
    ) {
      return false;
    }

    incrementEngagementCount();

    const payload = {
      data: {
        clientVersion,
        value: mapTableDataToUpdatePlanPayload(tableData),
        override: override ? true : undefined,
      },
    };

    const response = await onSaveSubjectPlan(payload);

    if (response instanceof Error) {
      if (response instanceof ApiError) {
        switch (response.status) {
          case 409: {
            const result = ConflictingPlanErrorResponseSchema.safeParse(
              response.data
            );

            if (!result.success) {
              captureAppError(result.error, {
                action: "save",
                area: "subject-plan",
                extra: {
                  component: "EditSubjectPlan",
                  subjectPlanId,
                },
              });
              addErrorToast({
                message: t("system-messages.update.error", { ns: COMMON_NS }),
              });
              return false;
            }
            setConflictingData(result.data);
            break;
          }
        }
      } else {
        captureAppError(response, {
          action: "save",
          area: "subject-plan",
          extra: {
            component: "EditSubjectPlan",
            subjectPlanId,
          },
        });
        addErrorToast({
          message: t("system-messages.update.error", { ns: COMMON_NS }),
        });
      }

      return false;
    } else {
      // used to force a refetch of the subject plan data, and allow the state to be updated with the new version
      handleRefreshSubjectPlan?.();
      addSuccessToast?.({
        message: t("system-messages.update.success", { ns: COMMON_NS }),
      });

      return true;
    }
  };

  const handleDiscardConflict = async () => {
    setConflictingData(null);
    await handleRefreshSubjectPlan?.();
    addSuccessToast?.({
      message: "Discarded changes and reloaded latest plan",
    });
  };

  if (isLoading) {
    return (
      <ContentContainer>
        <div className="text-center py-20">
          <Text as="p" variant="xl-medium">
            {t("system-messages.loading", { ns: COMMON_NS })}
          </Text>
        </div>
      </ContentContainer>
    );
  } else if (error || !curriculum || !subject) {
    captureAppError(error || new Error("Unknown error loading subject plan"), {
      action: "load",
      area: "subject-plan",
    });
    return <GenericError />;
  }

  return (
    <ContentContainer>
      <SubjectPlanHeader
        curriculum={curriculum}
        isLoading={isLoading}
        isUpdating={isUpdating}
        subject={subject}
        onUpdateSubjectStatus={onUpdateSubjectStatus}
      />
      {/* Info block */}
      <div
        aria-label="plan info subheader"
        className="max-w-[800px]"
        role="region"
      >
        <Text as="h2" variant="xl-medium">
          {t("table-pre-header.title", {
            curriculumName: curriculum.displayLabel,
          })}
        </Text>
        <Text as="p" className="mt-2">
          {t("table-pre-header.description")}
        </Text>
      </div>

      <div className="h-screen -mb-16 min-h-[600px]">
        {/* Filter actions */}
        <ResourceFilters resources={resources} />

        {/* Main content - planner and drawer */}
        <div className="flex border-b border-neutral-150 h-[calc(100vh-135px)]">
          {/* Planner table and actions */}
          <div
            aria-label={t("planner-table.label")}
            className="w-full flex flex-col gap-4 overflow-hidden border-r border-neutral-150 pr-6 pb-8 grow"
            role="region"
          >
            {/* Planner actions */}
            <div className="flex gap-4 justify-between mb-6">
              <div>
                <Button
                  size="sm"
                  variant="outlined"
                  onClick={() => setIsPlannerSettingsModalOpen(true)}
                >
                  <IconActionsSettings />
                  {t("actions.planner-settings")}
                </Button>
                {isPlannerSettingsModalOpen && (
                  <PlannerSettingsModalContainer
                    columnHeaders={orderedColumns}
                    open={isPlannerSettingsModalOpen}
                    rowHeaders={orderedRows}
                    onApply={onPlannerSettingsModalApply}
                    onClose={() => setIsPlannerSettingsModalOpen(false)}
                  />
                )}
              </div>
              <div className="flex gap-2">
                <Button
                  disabled={!canUndo}
                  size="sm"
                  variant="outlined"
                  onClick={onUndo}
                >
                  <Icon icon="Undo" />
                  {t("actions.undo", { ns: COMMON_NS })}
                </Button>
                <Button
                  disabled={!canRedo}
                  size="sm"
                  variant="outlined"
                  onClick={onRedo}
                >
                  {t("actions.redo", { ns: COMMON_NS })}
                  <Icon icon="Redo" />
                </Button>
                <Button
                  className="ml-1"
                  disabled={!hasUnsavedChanges || isUpdating || isLoading}
                  size="sm"
                  variant="primary"
                  onClick={() => handleSave()}
                >
                  {t("actions.save-changes", { ns: COMMON_NS })}
                </Button>
              </div>
            </div>

            {/* Planner table */}
            <div className="max-h-[calc(100vh-300px)] min-h-[300px] overflow-x-auto overflow-y-auto relative">
              <table
                ref={tableRef}
                className={[
                  "border-spacing-3 border-separate relative",
                  "**:[td,th]:whitespace-nowrap",
                  "**:[th]:ellipsis **:[th]:overflow-hidden",
                  "**:[th]:max-w-[170px] **:[th]:min-w-[170px] **:[th]:h-[84px] **:[th]:font-normal",
                  "**:[td]:w-[170px]  **:[td]:h-[84px] **:[td]:bg-neutral-100 **:[td]:rounded-[8px]",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                <thead className={theadBorderStyles}>
                  <tr className="h-[72px]">
                    <th
                      className={["py-150 px-300 text-left", firstColumnStyles]
                        .filter(Boolean)
                        .join(" ")}
                      role="columnheader"
                      scope="col"
                    >
                      <span className="sr-only">Cohorts</span>
                    </th>
                    {orderedColumns.map((ci) => (
                      <th
                        key={ci.id}
                        className="py-150 px-300 text-left min-w-[170px]"
                        role="columnheader"
                        scope="col"
                      >
                        {ci.title}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {orderedRows.map((row) => {
                    return (
                      <tr key={row.id}>
                        <th
                          className={firstColumnStyles}
                          role="rowheader"
                          scope="row"
                        >
                          <div className="text-left py-150 px-300 truncate max-w-[170px]">
                            {row.title}
                          </div>
                        </th>
                        {orderedColumns.map((col, index) => {
                          const formattedCellKey: FormattedCellKey = `${row.id}__${col.id}`;

                          // Skip rendering this cell if it's covered by a previous cell's span
                          if (skipSpannedCells.has(formattedCellKey)) {
                            return null;
                          }

                          const targetCell = tableData.cells.find(
                            (res) => res.column === col.id && res.row === row.id
                          );

                          if (targetCell?.isCustom) {
                            // Render custom cell
                            return (
                              // Find the corresponding cell and resource title
                              <td
                                key={col.id}
                                aria-label={`${row.title} ${col.title}`}
                                className="max-w-[170px]"
                                colSpan={targetCell.span}
                              >
                                <TableUnitCard
                                  cellBoundaries={cellBoundaries}
                                  cellId={targetCell.id}
                                  curriculumId={curriculum.id}
                                  customUnit={{
                                    id: targetCell.id,
                                    title: targetCell.title || "",
                                    description: targetCell.description,
                                    cohort: row.id,
                                    startOnColumn: col.id,
                                    span: targetCell.span || 1,
                                  }}
                                  meta={{
                                    row: row.id,
                                    column: col.id,
                                    cellIndex: index,
                                    cellSpan: targetCell.span ?? 1,
                                    cellId: targetCell.id,
                                  }}
                                  resource={{
                                    id: targetCell.id,
                                    name: targetCell.title || "Custom Unit",
                                    keyStagesSlug: [],
                                  }}
                                  subjectId={subject.id}
                                  tableData={{
                                    ...tableData,
                                    resources,
                                  }}
                                  onRemoveUnit={onRemoveUnit}
                                  onUpdateCustomUnit={onUpdateCustomUnit}
                                  onUpsertAdaptationNote={
                                    onUpsertAdaptationNote
                                  }
                                />
                              </td>
                            );
                          }

                          const targetResource = targetCell
                            ? resources.find(
                                (res) =>
                                  !targetCell.isCustom &&
                                  res.id === targetCell.resourceId
                              )
                            : null;

                          if (!targetCell || !targetResource) {
                            return (
                              <td
                                key={formattedCellKey}
                                aria-label={`${row.title} ${col.title}`}
                              >
                                <Droppable
                                  key={formattedCellKey}
                                  data={{
                                    target: "empty-table-cell",
                                    data: {
                                      row: row.id,
                                      column: col.id,
                                      cellIndex: index,
                                      cellBoundaries:
                                        cellBoundaries[formattedCellKey] ?? {},
                                    },
                                  }}
                                  id={formattedCellKey}
                                  render={({ isOver }) => (
                                    <div
                                      className={[
                                        "w-full h-full flex justify-center items-center rounded-[8px]",
                                        isOver ? "bg-neutral-150" : "",
                                      ]
                                        .filter(Boolean)
                                        .join(" ")}
                                    />
                                  )}
                                ></Droppable>
                              </td>
                            );
                          }

                          return (
                            // Find the corresponding cell and resource title
                            <td
                              key={col.id}
                              aria-label={`${row.title} ${col.title}`}
                              className="max-w-[170px]"
                              colSpan={targetCell.span}
                            >
                              <TableUnitCard
                                cellBoundaries={cellBoundaries}
                                cellId={targetCell.id}
                                curriculumId={curriculum.id}
                                meta={{
                                  resourceId: targetResource.id,
                                  row: row.id,
                                  column: col.id,
                                  cellIndex: index,
                                  cellSpan: targetCell.span ?? 1,
                                  cellId: targetCell.id,
                                }}
                                resource={targetResource}
                                schemeId={targetResource.id}
                                subjectId={subject.id}
                                tableData={{
                                  ...tableData,
                                  resources,
                                }}
                                onRemoveUnit={onRemoveUnit}
                                onUpsertAdaptationNote={onUpsertAdaptationNote}
                              />
                            </td>
                          );
                        })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* Drawer for adding units */}
          <UnitDrawer
            curriculumId={curriculum.id}
            resources={resources}
            subjectId={subject.id}
            tableData={tableData}
            onAddCustomUnit={onAddCustomUnit}
          />
        </div>

        {/* Sticky footer actions */}
        <div
          className={[
            "fixed bottom-0 right-0 bg-white border-t border-neutral-150 py-4 px-6 flex justify-end w-[calc(100%-48px)]",
            Z_INDEX_ORDERS.EDIT_SUBJECT_PLAN.FOOTER_ACTIONS,
          ].join(" ")}
        >
          <Button
            disabled={!hasUnsavedChanges || isUpdating || isLoading}
            variant="primary"
            onClick={() => handleSave()}
          >
            {t("actions.save-changes", { ns: COMMON_NS })}
          </Button>
        </div>
      </div>

      {conflictingData && (
        <ConflictResolutionModal
          conflictingData={conflictingData}
          handleConfirm={async () => {
            await handleSave({ override: true });
          }}
          onCancel={handleDiscardConflict}
          onClose={() => setConflictingData(null)}
        />
      )}

      {blockState === "blocked" ? (
        <UnsavedChangesModal
          onClose={abandonNavigation}
          onDiscardAndLeave={allowNavigation}
          onSaveAndLeave={async () => {
            const saved = await handleSave();

            if (saved) {
              allowNavigation();
            }
          }}
        />
      ) : null}
    </ContentContainer>
  );
};
